import type { MedplumClient } from "@medplum/core";
import type {
  ActivityDefinition,
  AllergyIntolerance,
  Appointment,
  Basic,
  CarePlan,
  ClinicalImpression,
  Communication,
  Condition,
  Device,
  DeviceDefinition,
  DocumentReference,
  Encounter,
  EpisodeOfCare,
  Flag,
  Goal,
  Location,
  MedicationStatement,
  Observation,
  ObservationDefinition,
  Patient,
  PlanDefinition,
  Practitioner,
  Procedure,
  Questionnaire,
  QuestionnaireResponse,
  RelatedPerson,
  RequestGroup,
  Schedule,
  ServiceRequest,
  Slot,
  Task,
} from "@medplum/fhirtypes";
import {
  ALLERGY_INTOLERANCE_ENTITY_ID_SYSTEM,
  CARE_PLAN_ENTITY_ID_SYSTEM,
  CARE_PLAN_GOAL_MEMBER_ID_SYSTEM,
  CASE_ENTITY_ID_SYSTEM,
  CONDITION_ENTITY_ID_SYSTEM,
  catalogModelKindOf,
  DEVICE_ENTITY_ID_SYSTEM,
  DIAGNOSTIC_DEFINITION_KIND,
  DIAGNOSTIC_ORDER_SET_ID_URL,
  DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM,
  DIAGNOSTIC_PROCEDURE_KIND,
  EXAM_ENTITY_ID_SYSTEM,
  FINDING_DEFINITION_KIND,
  fhirIdentifierIdentity,
  hasDiagnosticResourceKind,
  LOCATION_ENTITY_ID_SYSTEM,
  MEDICATION_STATEMENT_ENTITY_ID_SYSTEM,
  memberKey,
  OBSERVATION_ENTITY_ID_SYSTEM,
  ORDER_ID_URL,
  ORDER_SET_CURATION_RANK_URL,
  ORDER_SET_ENTITY_ID_SYSTEM,
  ORDER_SET_MEMBER_COUNT_URL,
  orderSetKindOf,
  PATIENT_DOCUMENT_ENTITY_ID_SYSTEM,
  PATIENT_ENTITY_ID_SYSTEM,
  PRACTITIONER_ENTITY_ID_SYSTEM,
  planDefinitionKindOf,
  QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM,
  QUESTIONNAIRE_INSTANCE_PROFILE,
  QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL,
  REDESIGNED_ORDER_SET_KIND,
  RELATED_PERSON_ENTITY_ID_SYSTEM,
  SCHEDULE_ENTITY_ID_SYSTEM,
  syncKey,
  TASK_ENTITY_ID_SYSTEM,
  TREATMENT_DEFINITION_KIND,
  TREATMENT_OBSERVATION_ID_SYSTEM,
  TREATMENT_ORDER_SET_ID_URL,
  TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM,
  TREATMENT_PROCEDURE_KIND,
  TREATMENT_PROCEDURE_KIND_SYSTEM,
  TREATMENT_UNCAPTURED_ID_SYSTEM,
  VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
} from "@repo/api";
import { codeSystemUrl } from "@repo/fhir-systems";
import { FINDING_ENTITY_ID_SYSTEM } from "@repo/fhir-systems/identifiers";
import { caseDomainId } from "../views/_case-reference";
import { clinicalNoteDomainId } from "../views/_clinical-note-reference";
import { patientDomainId } from "../views/_patient-reference";
import { visitDomainId } from "../views/_visit-reference";
import { createFhirCollectionConfig } from "./base";
import { CATALOG_VIEW_SCOPE } from "./catalog-view-scope";
import type { CanRead } from "./collection-scope";
import { FHIR_SEARCH_PAGE_SIZE } from "./constants";
import { deriveDeviceDefinitionFields } from "./device-definition-derived";
import { validateDiagnosticDefinitionRow } from "./diagnostic-definition-storage";
import { readFindingDefinitionRow } from "./finding-definition-storage";
import { isPerformanceMeasureRow, readPerformanceMeasureRow } from "./performance-measure-storage";
import { validateProgramDefinitionRow } from "./program-definition-storage";
import { deriveQuestionnaireFields } from "./questionnaire-derived";
import { assertQuestionnaireInstanceRowReadable } from "./questionnaire-instance-storage";
import { validateTreatmentDefinitionRow } from "./treatment-definition-storage";
import type {
  ActivityDefinitionDerived,
  AllergyIntoleranceDerived,
  AppointmentDerived,
  CarePlanDerived,
  ClinicalImpressionDerived,
  ConditionDerived,
  DeviceDefinitionDerived,
  DeviceDerived,
  DocumentReferenceDerived,
  EncounterDerived,
  EpisodeOfCareDerived,
  FindingDefinitionDerived,
  GoalDerived,
  LocationDerived,
  MedicationStatementDerived,
  ObservationDerived,
  PatientDerived,
  PlanDefinitionDerived,
  PractitionerDerived,
  ProcedureDerived,
  QuestionnaireDerived,
  QuestionnaireInstanceDerived,
  RelatedPersonDerived,
  RequestGroupDerived,
  ScheduleActorType,
  ScheduleDerived,
  ServiceRequestDerived,
  SlotDerived,
  TaskDerived,
} from "./types";

/**
 * Single module for every FHIR collection config in the data-store.
 *
 * One `createXCollectionConfig` export per FHIR resource type. Status-filtered
 * variants of the same resource (e.g. booked vs. availability-block slots)
 * live together as sibling exports.
 *
 * `deriveFields` hooks project scalar FK columns onto each row at sync time,
 * so live-query joins can use `eq(scalar, scalar)` instead of array-scanning
 * through FHIR's nested reference shapes. The resulting widened row types
 * (`AppointmentRow`, `EncounterRow`, `ScheduleRow`) live in `./types` and
 * flow through `FhirCollectionRegistry`.
 */

type Options = { medplum: MedplumClient; canRead?: CanRead };
const orderSetIdentity = fhirIdentifierIdentity<string>(ORDER_SET_ENTITY_ID_SYSTEM);
const locationIdentity = fhirIdentifierIdentity<string>(LOCATION_ENTITY_ID_SYSTEM);
const practitionerIdentity = fhirIdentifierIdentity<string>(PRACTITIONER_ENTITY_ID_SYSTEM);
const deviceIdentity = fhirIdentifierIdentity<string>(DEVICE_ENTITY_ID_SYSTEM);
const patientIdentity = fhirIdentifierIdentity<string>(PATIENT_ENTITY_ID_SYSTEM);
const patientDocumentIdentity = fhirIdentifierIdentity<string>(PATIENT_DOCUMENT_ENTITY_ID_SYSTEM);
const scheduleIdentity = fhirIdentifierIdentity<string>(SCHEDULE_ENTITY_ID_SYSTEM);
const caseIdentity = fhirIdentifierIdentity<string>(CASE_ENTITY_ID_SYSTEM);

// ── Private helpers ────────────────────────────────────────────────────────

function optionalIdentity(resource: { identifier?: Array<{ system?: string; value?: string }> }, system: string) {
  const values = (resource.identifier ?? []).flatMap((identifier) =>
    identifier.system === system && identifier.value ? [identifier.value] : [],
  );
  return values.length === 1 ? values[0] : undefined;
}

/** The one identity under `systems` carried by these references; two is ambiguous. */
function referencedIdentity(
  references: ReadonlyArray<{ identifier?: { system?: string; value?: string } } | undefined> | undefined,
  systems: string | readonly string[],
): string | undefined {
  const accepted = typeof systems === "string" ? [systems] : systems;
  const values = (references ?? []).flatMap((reference) =>
    reference?.identifier?.system && accepted.includes(reference.identifier.system) && reference.identifier.value
      ? [reference.identifier.value]
      : [],
  );
  return values.length === 1 ? values[0] : undefined;
}

const PROCEDURE_IDENTITY_SYSTEMS = [DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM, TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM];

function deriveAppointmentFields(apt: Appointment): AppointmentDerived {
  const patient = apt.participant?.find(
    (participant) => participant.actor?.identifier?.system === PATIENT_ENTITY_ID_SYSTEM,
  )?.actor;
  const caseReference = apt.supportingInformation?.find(
    (reference) => reference.identifier?.system === CASE_ENTITY_ID_SYSTEM,
  );
  const caseId = caseDomainId(caseReference);
  const visitId = optionalIdentity(apt, VISIT_ENTITY_ID_SYSTEM);
  const orderSetId = referencedIdentity(apt.supportingInformation, ORDER_SET_ENTITY_ID_SYSTEM);
  const slotKeys = visitId
    ? (apt.slot ?? []).flatMap((reference) =>
        reference.identifier?.system === VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM && reference.identifier.value
          ? [memberKey(visitId, reference.identifier.value)]
          : [],
      )
    : [];
  return {
    domainId: visitId,
    patientId: patientDomainId(patient) || undefined,
    ...(caseId ? { episodeOfCareId: caseId } : {}),
    orderSetId,
    orderSetJoinKey: orderSetId ?? `_noorderset_${apt.id}`,
    slotKeys,
    firstSlotJoinKey: slotKeys[0] ?? `_noslot_${apt.id}`,
  };
}

export function deriveEncounterFields(enc: Encounter): EncounterDerived {
  const caseReference = enc.episodeOfCare?.find((reference) => reference.identifier?.system === CASE_ENTITY_ID_SYSTEM);
  const caseId = caseDomainId(caseReference);
  const visitId = enc.appointment?.find((reference) => reference.identifier?.system === VISIT_ENTITY_ID_SYSTEM)
    ?.identifier?.value;
  return {
    visitId,
    visitJoinKey: visitId ?? `_novisit_${enc.id}`,
    patientId: patientDomainId(enc.subject) || undefined,
    ...(caseId ? { episodeOfCareId: caseId } : {}),
  };
}

function deriveClinicalImpressionFields(ci: ClinicalImpression): ClinicalImpressionDerived {
  const coding = ci.code?.coding?.[0];
  return {
    domainId: clinicalNoteDomainId(ci),
    visitId: visitDomainId(ci.encounter),
    codeCode: coding?.code,
    codeSystem: coding?.system,
  };
}

function deriveScheduleFields(schedule: Schedule): ScheduleDerived {
  const actor = schedule.actor?.[0];
  const actorSystem = actor?.identifier?.system;
  const referenceType = actor?.reference?.split("/")[0];
  const type: ScheduleActorType =
    actorSystem === PRACTITIONER_ENTITY_ID_SYSTEM
      ? "Practitioner"
      : actorSystem === LOCATION_ENTITY_ID_SYSTEM
        ? "Location"
        : actorSystem === DEVICE_ENTITY_ID_SYSTEM
          ? "Device"
          : referenceType === "Practitioner" || referenceType === "Location" || referenceType === "Device"
            ? referenceType
            : "Other";
  const id =
    actorSystem === PRACTITIONER_ENTITY_ID_SYSTEM ||
    actorSystem === LOCATION_ENTITY_ID_SYSTEM ||
    actorSystem === DEVICE_ENTITY_ID_SYSTEM
      ? actor?.identifier?.value
      : undefined;
  return {
    domainId: scheduleIdentity.read(schedule),
    actorType: type,
    actorId: id,
    practitionerId: type === "Practitioner" ? id : undefined,
    practitionerDomainId:
      actor?.identifier?.system === PRACTITIONER_ENTITY_ID_SYSTEM ? actor.identifier.value : undefined,
    actorDisplay: actor?.display,
    locationId: type === "Location" ? id : undefined,
    deviceId: type === "Device" ? id : undefined,
  };
}

// ── Collection factories ───────────────────────────────────────────────────

export function createAppointmentCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Appointment, AppointmentDerived>({
    resourceType: "Appointment",
    ...options,
    searchParams: { _count: "1000" },
    deriveFields: deriveAppointmentFields,
  });
}

export function createEncounterCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Encounter, EncounterDerived>({
    resourceType: "Encounter",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveEncounterFields,
  });
}

export function createScheduleCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Schedule, ScheduleDerived>({
    resourceType: "Schedule",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveScheduleFields,
  });
}

export function deriveSlotFields(slot: Slot): SlotDerived {
  const scheduleDomainId = referencedIdentity([slot.schedule], SCHEDULE_ENTITY_ID_SYSTEM);
  return {
    scheduleDomainId,
    scheduleJoinKey: scheduleDomainId ?? `_noschedule_${slot.id}`,
    slotKey: syncKey(slot),
  };
}

/** Booked appointment slots — `Slot.status = busy`. */
export function createBookedSlotCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Slot, SlotDerived>({
    resourceType: "Slot",
    ...options,
    collectionName: "booked-slot",
    searchParams: { status: "busy", _count: FHIR_SEARCH_PAGE_SIZE },
    matches: (slot) => slot.status === "busy",
    deriveFields: deriveSlotFields,
  });
}

/** Availability blocks — `Slot.status = busy-unavailable`. */
export function createAvailabilityBlockCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Slot>({
    resourceType: "Slot",
    ...options,
    collectionName: "availability-block",
    searchParams: { status: "busy-unavailable", _count: FHIR_SEARCH_PAGE_SIZE },
    matches: (slot) => slot.status === "busy-unavailable",
  });
}

/**
 * Unified Slot collection — covers both `busy` (booked) and `busy-unavailable`
 * (blocked) statuses in a single FHIR-resource-typed collection. Views/queries
 * discriminate by status via `where(eq(s.status, ...))`.
 *
 * Replaces the legacy split between `bookedSlot` and `availability-block`.
 */
export function createSlotCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Slot, SlotDerived>({
    resourceType: "Slot",
    ...options,
    searchParams: { status: "busy,busy-tentative,busy-unavailable", _count: FHIR_SEARCH_PAGE_SIZE },
    matches: (slot) => ["busy", "busy-tentative", "busy-unavailable"].includes(slot.status),
    deriveFields: deriveSlotFields,
  });
}

/**
 * ADR-0021: the diagnostic and treatment projections run inside a live query's
 * `fn.select`, where a throw blanks the page. Reading everything they need here
 * instead means an unreadable row is dropped at sync — `deriveForSync` in
 * `./base.ts` logs its id and skips it — rather than reaching them.
 *
 * Guarded per kind. This hook runs for EVERY `ActivityDefinition`, and a row
 * neither view projects owes neither view's fields; validating it would drop
 * rows nothing is even reading.
 *
 * Neither diagnostic nor treatment has a visible-warning path of the sort
 * `projectProtocolDefinitionRows` feeds — both project inside `fn.select`, so
 * there is nowhere for a per-row failure to surface — which is why dropping at
 * sync is the loudest option available to them.
 *
 * `catalogVersion` substitutes `0` for an unparseable version. Inside these two
 * branches the substitution is already dead: both validators read the version
 * through a storage reader that throws on exactly that row, so it never reaches
 * this line. It stays for the generic path, where no version may be legitimate.
 */
export function deriveActivityDefinitionFields(definition: ActivityDefinition): ActivityDefinitionDerived {
  const modelKind = catalogModelKindOf(definition);
  if (modelKind === DIAGNOSTIC_DEFINITION_KIND) validateDiagnosticDefinitionRow(definition);
  if (modelKind === TREATMENT_DEFINITION_KIND) validateTreatmentDefinitionRow(definition);
  return {
    catalogViewScope: CATALOG_VIEW_SCOPE,
    catalogModelKind: modelKind,
    catalogVersion: Number.isFinite(Number(definition.version)) ? Number(definition.version) : 0,
    definitionCanonical: definition.url && definition.version ? `${definition.url}|${definition.version}` : undefined,
  };
}

export function createActivityDefinitionCollectionConfig(options: Options) {
  return createFhirCollectionConfig<ActivityDefinition, ActivityDefinitionDerived>({
    resourceType: "ActivityDefinition",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveActivityDefinitionFields,
  });
}

function deriveCarePlanFields(cp: CarePlan): CarePlanDerived {
  const eocReference = cp.supportingInfo?.find((reference) => reference.identifier?.system === CASE_ENTITY_ID_SYSTEM);
  const caseId = caseDomainId(eocReference);
  return {
    domainId: cp.identifier?.find((identifier) => identifier.system === CARE_PLAN_ENTITY_ID_SYSTEM)?.value,
    patientId: patientDomainId(cp.subject) || undefined,
    ...(caseId ? { episodeOfCareId: caseId } : {}),
    // Case CarePlan = no parent (basedOn) and no encounter link. OrderSet curation
    // is case-scoped directly and does not participate in this discriminator.
    isCaseCarePlan: !cp.basedOn?.length && !cp.encounter,
  };
}

export function createCarePlanCollectionConfig(options: Options) {
  return createFhirCollectionConfig<CarePlan, CarePlanDerived>({
    resourceType: "CarePlan",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveCarePlanFields,
  });
}

export function deriveGoalFields(goal: Goal): GoalDerived {
  const carePlanId = optionalIdentity(goal, CARE_PLAN_ENTITY_ID_SYSTEM);
  return {
    memberId: goal.identifier?.find((identifier) => identifier.system === CARE_PLAN_GOAL_MEMBER_ID_SYSTEM)?.value,
    patientId: patientDomainId(goal.subject),
    carePlanId,
    carePlanJoinKey: carePlanId ?? `_nocareplan_${goal.id}`,
  };
}

export function createGoalCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Goal, GoalDerived>({
    resourceType: "Goal",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveGoalFields,
  });
}

export function createFlagCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Flag>({
    resourceType: "Flag",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
  });
}

export function createClinicalImpressionCollectionConfig(options: Options) {
  return createFhirCollectionConfig<ClinicalImpression, ClinicalImpressionDerived>({
    resourceType: "ClinicalImpression",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveClinicalImpressionFields,
  });
}

export function createCommunicationCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Communication>({
    resourceType: "Communication",
    ...options,
    searchParams: { _sort: "-sent", _count: "50" },
  });
}

export function createConditionCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Condition, ConditionDerived>({
    resourceType: "Condition",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (condition) => ({
      domainId: condition.identifier?.find((identifier) => identifier.system === CONDITION_ENTITY_ID_SYSTEM)?.value,
      visitId: visitDomainId(condition.encounter),
    }),
  });
}

export function createDeviceCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Device, DeviceDerived>({
    resourceType: "Device",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (device) => ({
      domainId: deviceIdentity.read(device),
      locationDomainId:
        device.location?.identifier?.system === LOCATION_ENTITY_ID_SYSTEM
          ? device.location.identifier.value
          : undefined,
    }),
  });
}

export function createDeviceDefinitionCollectionConfig(options: Options) {
  return createFhirCollectionConfig<DeviceDefinition, DeviceDefinitionDerived>({
    resourceType: "DeviceDefinition",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveDeviceDefinitionFields,
  });
}

function deriveEpisodeOfCareFields(eoc: EpisodeOfCare): EpisodeOfCareDerived {
  const patientId = patientDomainId(eoc.patient) || undefined;
  return {
    domainId: caseIdentity.read(eoc),
    patientId,
    patientJoinKey: patientId ?? `_nopatient_${eoc.id}`,
  };
}

export function createEpisodeOfCareCollectionConfig(options: Options) {
  return createFhirCollectionConfig<EpisodeOfCare, EpisodeOfCareDerived>({
    resourceType: "EpisodeOfCare",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveEpisodeOfCareFields,
  });
}

export function createLocationCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Location, LocationDerived>({
    resourceType: "Location",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (location) => ({
      domainId: locationIdentity.read(location),
      partOfDomainId:
        location.partOf?.identifier?.system === LOCATION_ENTITY_ID_SYSTEM
          ? location.partOf.identifier.value
          : undefined,
    }),
  });
}

export function createMedicationStatementCollectionConfig(options: Options) {
  return createFhirCollectionConfig<MedicationStatement, MedicationStatementDerived>({
    resourceType: "MedicationStatement",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (statement) => ({
      domainId: statement.identifier?.find((identifier) => identifier.system === MEDICATION_STATEMENT_ENTITY_ID_SYSTEM)
        ?.value,
    }),
  });
}

export function createAllergyIntoleranceCollectionConfig(options: Options) {
  return createFhirCollectionConfig<AllergyIntolerance, AllergyIntoleranceDerived>({
    resourceType: "AllergyIntolerance",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (allergy) => ({
      domainId: allergy.identifier?.find((identifier) => identifier.system === ALLERGY_INTOLERANCE_ENTITY_ID_SYSTEM)
        ?.value,
    }),
  });
}

export function createDocumentReferenceCollectionConfig(options: Options) {
  return createFhirCollectionConfig<DocumentReference, DocumentReferenceDerived>({
    resourceType: "DocumentReference",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (document) => ({
      domainId: patientDocumentIdentity.read(document),
      patientId: patientDomainId(document.subject),
      visitId: visitDomainId(document.context?.encounter?.[0]),
    }),
  });
}

export function deriveObservationFields(observation: Observation): ObservationDerived {
  const order = observation.basedOn?.find((reference) => reference.identifier?.system === ORDER_ID_URL);
  // Read the parent identity the way the mapper writes it (extension), and
  // fall back to the identifier slot the way the mapper's own read does.
  const orderSetId =
    order?.extension?.find((extension) => extension.url === ORDER_SET_ENTITY_ID_SYSTEM)?.valueString ??
    observation.basedOn?.find((reference) => reference.identifier?.system === ORDER_SET_ENTITY_ID_SYSTEM)?.identifier
      ?.value;
  const procedureId = referencedIdentity(observation.partOf, PROCEDURE_IDENTITY_SYSTEMS);
  return {
    domainId: observation.identifier?.find((identifier) =>
      [OBSERVATION_ENTITY_ID_SYSTEM as string, FINDING_ENTITY_ID_SYSTEM, EXAM_ENTITY_ID_SYSTEM].includes(
        identifier.system ?? "",
      ),
    )?.value,
    patientId: patientDomainId(observation.subject),
    findingInstance:
      observation.identifier?.some((identifier) => identifier.system === FINDING_ENTITY_ID_SYSTEM) === true,
    examInstance: observation.identifier?.some((identifier) => identifier.system === EXAM_ENTITY_ID_SYSTEM) === true,
    procedureId,
    procedureJoinKey: procedureId ?? `_noprocedure_${observation.id}`,
    treatmentObservationId: observation.identifier?.find(
      (identifier) => identifier.system === TREATMENT_OBSERVATION_ID_SYSTEM,
    )?.value,
    definitionId: observation.code?.coding?.find(
      (coding) => coding.system === codeSystemUrl("treatment-observation-definition"),
    )?.code,
    lifecycle:
      observation.status === "entered-in-error"
        ? "EnteredInError"
        : observation.status === "corrected"
          ? "Corrected"
          : "Final",
    uncaptured: observation.identifier?.some((identifier) => identifier.system === TREATMENT_UNCAPTURED_ID_SYSTEM),
    visitId: visitDomainId(observation.encounter),
    orderId: order?.identifier?.value,
    ...orderJoinKeys(observation.id, orderSetId, order?.identifier?.value),
  };
}

export function createObservationCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Observation, ObservationDerived>({
    resourceType: "Observation",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveObservationFields,
  });
}

/**
 * Basic resources used as chart-edit locks (advisory locks per visit or
 * patient, keyed by identifier). Filtered by code so we don't sync every Basic
 * in the project — only the lock instances that the lock state machine cares
 * about.
 */
export function createBasicCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Basic>({
    matches: (basic) =>
      basic.code.coding?.some(
        (code) => code.system === codeSystemUrl("lock-type") && code.code === "chart-edit-lock",
      ) ?? false,
    resourceType: "Basic",
    ...options,
    searchParams: {
      code: `${codeSystemUrl("lock-type")}|chart-edit-lock`,
      _count: FHIR_SEARCH_PAGE_SIZE,
    },
  });
}

export function createPatientCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Patient, PatientDerived>({
    resourceType: "Patient",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (patient) => ({ domainId: patientIdentity.read(patient) }),
  });
}

export function derivePlanDefinitionFields(definition: PlanDefinition): PlanDefinitionDerived {
  const programProtocolUrls = [
    ...new Set(
      (definition.action ?? []).flatMap((day) =>
        (day.action ?? []).flatMap((session) => {
          const canonical = session.definitionCanonical;
          return canonical && !canonical.includes("|") ? [canonical] : [];
        }),
      ),
    ),
  ];
  const definitionKind = planDefinitionKindOf(definition);
  // ADR-0021: the program projection runs inside a live query, where a throw
  // blanks the page. Reading everything it needs here instead means an
  // unreadable row is dropped at sync rather than reaching it.
  if (definitionKind === "program") validateProgramDefinitionRow(definition);
  return {
    catalogViewScope: CATALOG_VIEW_SCOPE,
    catalogModelKind: catalogModelKindOf(definition),
    catalogVersion: Number(definition.version),
    ...(definition.url && definition.version ? { definitionCanonical: `${definition.url}|${definition.version}` } : {}),
    definitionKind,
    programProtocolUrls,
  };
}

export function createPlanDefinitionCollectionConfig(options: Options) {
  return createFhirCollectionConfig<PlanDefinition, PlanDefinitionDerived>({
    resourceType: "PlanDefinition",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: derivePlanDefinitionFields,
  });
}

export function createPractitionerCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Practitioner, PractitionerDerived>({
    resourceType: "Practitioner",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (practitioner) => ({ domainId: practitionerIdentity.read(practitioner) }),
  });
}

export function createProcedureCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Procedure, ProcedureDerived>({
    resourceType: "Procedure",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveProcedureFields,
  });
}

/**
 * The two halves of the order join, both guaranteed defined.
 *
 * `orderId` is a member id scoped to its OrderSet — every set realized from one
 * protocol mints the same one — so a child must be matched on the PARENT
 * identity as well or a patient's second set renders the first one's charting.
 * The sentinels keep an unlinked child from matching every other unlinked child
 * (`eq(undefined, undefined)` fans out); they are per-row, so they match nothing
 * but themselves. Row ids are private locators used here purely to make a
 * non-matching key unique — they are never read back as identity.
 */
export function orderJoinKeys(
  rowId: string | undefined,
  orderSetId: string | undefined,
  orderId: string | undefined,
): { orderSetJoinKey: string; orderJoinKey: string } {
  return {
    orderSetJoinKey: orderSetId ?? `_noorderset_${rowId ?? ""}`,
    orderJoinKey: orderId ?? `_noorder_${rowId ?? ""}`,
  };
}

export function deriveProcedureFields(procedure: Procedure): ProcedureDerived {
  const isTreatment = procedure.meta?.tag?.some(
    (tag) => tag.system === TREATMENT_PROCEDURE_KIND_SYSTEM && tag.code === TREATMENT_PROCEDURE_KIND,
  );
  const isDiagnostic = hasDiagnosticResourceKind(procedure, DIAGNOSTIC_PROCEDURE_KIND);
  // The parent OrderSet identity the mapper already stamps on the record —
  // treatment and diagnostic each carry it under their own extension.
  const orderSetId = procedure.extension?.find(
    (extension) => extension.url === (isDiagnostic ? DIAGNOSTIC_ORDER_SET_ID_URL : TREATMENT_ORDER_SET_ID_URL),
  )?.valueString;
  const orderId = procedure.basedOn?.find((reference) => reference.identifier?.system === ORDER_ID_URL)?.identifier
    ?.value;
  return {
    ...(isDiagnostic
      ? {
          domainId: optionalIdentity(procedure, DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM),
        }
      : isTreatment
        ? {
            domainId: optionalIdentity(procedure, TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM),
          }
        : {}),
    patientId: patientDomainId(procedure.subject) || undefined,
    orderId,
    ...orderJoinKeys(procedure.id, orderSetId, orderId),
    visitId: visitDomainId(procedure.encounter),
  };
}

export function createQuestionnaireCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Questionnaire, QuestionnaireDerived>({
    resourceType: "Questionnaire",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveQuestionnaireFields,
  });
}

export function createQuestionnaireInstanceCollectionConfig(options: Options) {
  return createFhirCollectionConfig<QuestionnaireResponse, QuestionnaireInstanceDerived>({
    resourceType: "QuestionnaireResponse",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveQuestionnaireInstanceFields,
  });
}

export function deriveQuestionnaireInstanceFields(response: QuestionnaireResponse): QuestionnaireInstanceDerived {
  if (isPerformanceMeasureRow(response)) readPerformanceMeasureRow(response);
  if (response.meta?.profile?.includes(QUESTIONNAIRE_INSTANCE_PROFILE))
    assertQuestionnaireInstanceRowReadable(response);
  const [definitionUrl, definitionVersion] = response.questionnaire?.split("|") ?? [];
  const identifier = response.identifier;
  const underAmendment = response.extension?.some(
    (extension) => extension.url === QUESTIONNAIRE_INSTANCE_UNDER_AMENDMENT_URL && extension.valueBoolean === true,
  );
  const status =
    response.status === "in-progress"
      ? underAmendment
        ? "underAmendment"
        : "inProgress"
      : response.status === "entered-in-error"
        ? "enteredInError"
        : response.status;
  return {
    questionnaireInstance: response.meta?.profile?.includes(QUESTIONNAIRE_INSTANCE_PROFILE) ?? false,
    ...(response.meta?.profile?.includes(QUESTIONNAIRE_INSTANCE_PROFILE)
      ? {
          domainId: identifier?.system === QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM ? identifier.value : undefined,
        }
      : {}),
    patientId: patientDomainId(response.subject),
    visitId: visitDomainId(response.encounter),
    definitionRef: response.questionnaire,
    definitionUrl,
    definitionVersion,
    instanceAssignedAt: response.authored ?? "",
    instanceStatus: status,
    instanceOpen: status === "inProgress" || status === "underAmendment",
  };
}

export function deriveRequestGroupFields(rg: RequestGroup): RequestGroupDerived {
  const canonical = rg.instantiatesCanonical?.[0];
  const [url, version] = canonical?.split("|") ?? [];
  const carePlanReference = rg.basedOn?.find(
    (reference) => reference.identifier?.system === CARE_PLAN_ENTITY_ID_SYSTEM,
  );
  const carePlanId =
    carePlanReference?.identifier?.system === CARE_PLAN_ENTITY_ID_SYSTEM
      ? carePlanReference.identifier.value
      : undefined;
  const curationRank = rg.extension?.find((extension) => extension.url === ORDER_SET_CURATION_RANK_URL)?.valueInteger;
  const memberCount = rg.extension?.find((extension) => extension.url === ORDER_SET_MEMBER_COUNT_URL)?.valueInteger;
  const isRedesignedOrderSet = orderSetKindOf(rg) === REDESIGNED_ORDER_SET_KIND;
  return {
    isRedesignedOrderSet,
    ...(isRedesignedOrderSet ? { domainId: orderSetIdentity.read(rg) } : {}),
    patientId: patientDomainId(rg.subject) || undefined,
    visitId: visitDomainId(rg.encounter),
    ...(canonical ? { protocolCanonical: canonical } : {}),
    ...(url ? { protocolUrl: url } : {}),
    ...(version ? { protocolVersion: version } : {}),
    ...(carePlanId ? { carePlanId } : {}),
    ...(carePlanId && curationRank !== undefined ? { curationRank } : {}),
    ...(memberCount !== undefined ? { memberCount } : {}),
  };
}

export function createRequestGroupCollectionConfig(options: Options) {
  return createFhirCollectionConfig<RequestGroup, RequestGroupDerived>({
    resourceType: "RequestGroup",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveRequestGroupFields,
  });
}

export function createServiceRequestCollectionConfig(options: Options) {
  return createFhirCollectionConfig<ServiceRequest, ServiceRequestDerived>({
    resourceType: "ServiceRequest",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (request) => {
      const orderSetId = referencedIdentity(request.basedOn, ORDER_SET_ENTITY_ID_SYSTEM);
      return {
        orderSetId,
        orderSetJoinKey: orderSetId ?? `_noorderset_${request.id}`,
        orderId: request.extension?.find((extension) => extension.url === ORDER_ID_URL)?.valueString,
        definitionCanonical: request.instantiatesCanonical?.[0],
      };
    },
  });
}

function deriveTaskFields(task: Task): TaskDerived {
  return {
    domainId: task.identifier?.find((identifier) => identifier.system === TASK_ENTITY_ID_SYSTEM)?.value,
    patientId: patientDomainId(task.for) || undefined,
  };
}

export function createTaskCollectionConfig(options: Options) {
  return createFhirCollectionConfig<Task, TaskDerived>({
    resourceType: "Task",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveTaskFields,
  });
}

export function deriveFindingDefinitionFields(row: ObservationDefinition): FindingDefinitionDerived {
  const facts = readFindingDefinitionRow(row);
  return {
    url: facts.spine.url,
    title: facts.spine.name,
    description: facts.spine.description,
    status: facts.spine.availability === "Active" ? "active" : "retired",
    catalogViewScope: CATALOG_VIEW_SCOPE,
    catalogModelKind: FINDING_DEFINITION_KIND,
    catalogVersion: facts.spine.version,
    catalogLineage: facts.spine.url,
    findingDefinitionRef: `${facts.spine.url}|${facts.spine.version}`,
  };
}
export function createFindingDefinitionCollectionConfig(options: Options) {
  return createFhirCollectionConfig<ObservationDefinition, FindingDefinitionDerived>({
    resourceType: "ObservationDefinition",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: deriveFindingDefinitionFields,
  });
}

export function createRelatedPersonCollectionConfig(options: Options) {
  return createFhirCollectionConfig<RelatedPerson, RelatedPersonDerived>({
    resourceType: "RelatedPerson",
    ...options,
    searchParams: { _count: FHIR_SEARCH_PAGE_SIZE },
    deriveFields: (person) => ({ domainId: optionalIdentity(person, RELATED_PERSON_ENTITY_ID_SYSTEM) }),
  });
}
